import { yup } from '@framework/validation/initYup.ts';
import { FormBuilderMetaFlags } from '../../../../../validator/form-builder/flags/FormBuilderMetaFlags.ts';
import { getWidgetName } from '../../../../../validator/form-builder/getWidgetName.ts';
import { setOptionEnabledCondition } from '../../../../../validator/setEnabledCondition.ts';
import { setOptionVisibleCondition } from '../../../../../validator/setVisibleCondition.ts';
import { setWidgetOptionSchemaLabel } from '../../../../../validator/utils/setWidgetSchemaLabel.ts';
import { type IWidgetOptionValue } from '../../../Widget.ts';
import { isWidgetOptionValue } from '../common/option-list/isWidgetOptionValue.ts';
import { createMultiSelectObjectSchema } from './createMultiSelectObjectSchema.ts';
import { multiSelectOptionsObjectName } from './getMultiSelectOptionsObjectName.ts';
import { type IMultiSelectWidget } from './IMultiSelectWidget.ts';

export function getMultiSelectOptionsWidgetSchema(widget: IMultiSelectWidget) {
  const name = getWidgetName(widget);

  const optionsObjectName = multiSelectOptionsObjectName(name);

  const valueOptions = widget.options.filter(isWidgetOptionValue);
  const possibleOptions = valueOptions.map(o => o.value);

  // 1. Schema a lista, in read-only, solo per la registrazione sui
  // `serializedValues` e per l'uso nelle Regole condizionali
  const listSchema = yup
    .array()
    .formBuilderFlags(
      FormBuilderMetaFlags.SkipDefaultValue,
      FormBuilderMetaFlags.SkipEnabledCondition,
    )
    .meta({
      [FormBuilderMetaFlags.SerializeSiblingOptions]: possibleOptions,
    })
    .of(yup.string().oneOf(possibleOptions))
    .default(() => [])
    .when([optionsObjectName], ([optionsObjectValue], schema) => {
      return schema.transform(() => {
        const selectedOptions = Object.entries(optionsObjectValue)
          .filter(([key, value]) => value)
          .map(([key, value]) => key);
        return selectedOptions;
      });
    });

  // 2. Schema a oggetto, con una chiave per ogni opzione: utilizzato per
  // il rendering del widget, vedi `CheckboxOptionInput`
  const optionsObjectSchema = createMultiSelectObjectSchema({
    widget,
    optionsObjectName,
    optionsWithKeys: valueOptions.map(o => ({
      option: o,
      key: o.value,
    })),
    createOptionSchema,
  });

  return {
    [name]: listSchema.notRequired(),
    [optionsObjectName]: optionsObjectSchema.notRequired(),
  };
}

function createOptionSchema(
  parentName: string,
  widget: IMultiSelectWidget,
  option: IWidgetOptionValue,
) {
  let schema = yup.boolean().notRequired();
  schema = setOptionEnabledCondition(schema, widget, option);
  schema = setOptionVisibleCondition(schema, widget, option);
  schema = setWidgetOptionSchemaLabel(option, schema);
  return schema;
}
